// src/features/error-definitions/components/ErrorDefinitionDialog.tsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslate, useDataProvider, useNotify } from 'react-admin';
import type {
  ErrorDefinition,
  ErrorSeverity,
  ErrorCategory,
  ErrorNature,
  ErrorDefinitionTranslation,
  SupportedLanguage,
  MatchCondition,
  MatchConditionOperator,
} from '@/types/errorDefinitions';
import { HelpPopover } from '@/components/common';

const SEVERITY_OPTIONS: ErrorSeverity[] = ['Critical', 'Warning', 'Info'];
const CATEGORY_OPTIONS: ErrorCategory[] = ['Encoder', 'Mechanical', 'Card', 'Room', 'Network', 'System', 'PMS', 'Locks', 'QR', 'Payments', 'Identification'];
const NATURE_OPTIONS: ErrorNature[] = ['Error', 'Operational', 'Informational'];
// Only "Equals" is supported today; dropdown is ready for future operators.
const OPERATOR_OPTIONS: MatchConditionOperator[] = ['Equals'];
const LANGUAGE_OPTIONS: { code: SupportedLanguage; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
];

interface ErrorDefinitionDialogProps {
  open: boolean;
  definition: ErrorDefinition | null;
  onClose: () => void;
  onSave: () => void;
}

interface TranslationFormData {
  id?: string; // Preserve translation ID for updates
  language: SupportedLanguage;
  title: string;
  description: string;
  recommendation: string;
}

interface ConditionFormData {
  id?: string; // Preserve condition ID for updates
  metadataKey: string;
  metadataValue: string;
  operator: MatchConditionOperator;
  isActive: boolean;
}

/**
 * Dialog for creating/editing error definitions
 */
export const ErrorDefinitionDialog: React.FC<ErrorDefinitionDialogProps> = ({
  open,
  definition,
  onClose,
  onSave,
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const isEditing = !!definition;

  // Form state - only basic info and translations
  const [errorCode, setErrorCode] = useState('');
  const [severity, setSeverity] = useState<ErrorSeverity>('Warning');
  const [category, setCategory] = useState<ErrorCategory>('System');
  const [nature, setNature] = useState<ErrorNature>('Error');
  const [translations, setTranslations] = useState<TranslationFormData[]>([]);
  const [conditions, setConditions] = useState<ConditionFormData[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes or definition changes
  useEffect(() => {
    if (open) {
      if (definition) {
        // Editing mode - populate form
        setErrorCode(definition.errorCode);
        setSeverity(definition.severity);
        setCategory(definition.category);
        setNature((definition.nature as ErrorNature) || 'Error');
        // Map translations - API returns languageCode, we use language
        // Preserve the id for updates
        setTranslations(
          (definition.translations || []).map((t) => ({
            id: t.id, // Preserve translation ID for updates
            language: (t.language || t.languageCode) as SupportedLanguage,
            title: t.title || '',
            description: t.description || '',
            recommendation: t.recommendation || t.suggestedAction || '',
          }))
        );
        setConditions(
          (definition.conditions || []).map((c) => ({
            id: c.id,
            metadataKey: c.metadataKey || '',
            metadataValue: c.metadataValue || '',
            operator: c.operator || 'Equals',
            isActive: c.isActive !== false,
          }))
        );
        setActiveTab(0);
      } else {
        // Create mode - reset form with English translation by default
        setErrorCode('');
        setSeverity('Warning');
        setCategory('System');
        setNature('Error');
        setTranslations([
          { language: 'en', title: '', description: '', recommendation: '' },
        ]);
        setConditions([]);
        setActiveTab(0);
      }
      setError(null);
    }
  }, [open, definition]);

  // Handlers
  const handleAddTranslation = () => {
    const usedLanguages = translations.map((t) => t.language);
    const availableLanguage = LANGUAGE_OPTIONS.find((l) => !usedLanguages.includes(l.code));

    if (availableLanguage) {
      setTranslations([
        ...translations,
        { language: availableLanguage.code, title: '', description: '', recommendation: '' },
      ]);
      setActiveTab(translations.length);
    }
  };

  const handleRemoveTranslation = (index: number) => {
    const translation = translations[index];
    // Don't allow removing if it's the only translation or if it's English
    if (translations.length > 1 && translation.language !== 'en') {
      const newTranslations = translations.filter((_, i) => i !== index);
      setTranslations(newTranslations);
      if (activeTab >= newTranslations.length) {
        setActiveTab(newTranslations.length - 1);
      }
    }
  };

  const handleTranslationChange = (
    index: number,
    field: keyof TranslationFormData,
    value: string
  ) => {
    const newTranslations = [...translations];
    newTranslations[index] = { ...newTranslations[index], [field]: value };
    setTranslations(newTranslations);
  };

  // Condition handlers
  const handleAddCondition = () => {
    setConditions([
      ...conditions,
      { metadataKey: '', metadataValue: '', operator: 'Equals', isActive: true },
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleConditionChange = (
    index: number,
    field: keyof ConditionFormData,
    value: string | boolean
  ) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setConditions(newConditions);
  };

  const validateForm = (): boolean => {
    if (!errorCode.trim()) {
      setError(translate('resources.errorDefinitions.validation.errorCodeRequired'));
      return false;
    }

    // Check error code format (uppercase with underscores)
    if (!/^[A-Z][A-Z0-9_]*$/.test(errorCode)) {
      setError(translate('resources.errorDefinitions.validation.errorCodeFormat'));
      return false;
    }

    // Check English translation has at least a title
    const enTranslation = translations.find((t) => t.language === 'en');
    if (!enTranslation || !enTranslation.title.trim()) {
      setError(translate('resources.errorDefinitions.validation.englishRequired'));
      return false;
    }

    // Every condition row must have both key and value
    const hasIncompleteCondition = conditions.some(
      (c) => !c.metadataKey.trim() || !c.metadataValue.trim()
    );
    if (hasIncompleteCondition) {
      setError(translate('resources.errorDefinitions.validation.conditionIncomplete'));
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      // Build translation data with languageCode (API field name)
      // Include id for existing translations to update them, omit for new ones
      const translationData = translations
        .filter((t) => t.title.trim())
        .map((t) => ({
          ...(t.id ? { id: t.id } : {}), // Include id only if it exists (for updates)
          languageCode: t.language, // API expects languageCode, not language
          title: t.title.trim(),
          description: t.description.trim(),
          recommendation: t.recommendation.trim() || undefined,
        }));

      // Build condition data (empty rows already rejected by validation).
      const conditionData: MatchCondition[] = conditions.map((c) => ({
        metadataKey: c.metadataKey.trim(),
        metadataValue: c.metadataValue.trim(),
        operator: c.operator,
      }));

      if (isEditing && definition) {
        // Update existing - PUT expects GUID id, not errorCode
        const definitionId = definition.id;

        console.log('Updating definition:', { id: definitionId, errorCode: definition.errorCode });

        if (!definitionId) {
          throw new Error('Cannot update: missing definition ID');
        }

        await dataProvider.updateErrorDefinition(definitionId, {
          severity,
          category,
          nature,
          translations: translationData as ErrorDefinitionTranslation[],
          // REPLACE-ALL: send the full list including ids and active state.
          conditions: conditions.map((c) => ({
            ...(c.id ? { id: c.id } : {}),
            metadataKey: c.metadataKey.trim(),
            metadataValue: c.metadataValue.trim(),
            operator: c.operator,
            isActive: c.isActive,
          })),
        });
        notify('resources.errorDefinitions.notifications.updated', { type: 'success' });
      } else {
        // Create new - deviceType "*" means applies to all device types
        await dataProvider.createErrorDefinition({
          errorCode: errorCode.trim(),
          deviceType: '*',
          severity,
          category,
          nature,
          translations: translationData as ErrorDefinitionTranslation[],
          conditions: conditionData,
        });
        notify('resources.errorDefinitions.notifications.created', { type: 'success' });
      }

      onSave();
    } catch (err: any) {
      console.error('Error saving error definition:', err);
      setError(err.message || translate('resources.errorDefinitions.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const usedLanguages = translations.map((t) => t.language);
  const canAddTranslation = usedLanguages.length < LANGUAGE_OPTIONS.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isEditing
          ? translate('resources.errorDefinitions.dialog.editTitle')
          : translate('resources.errorDefinitions.dialog.createTitle')
        }
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Basic Information */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
          <Typography variant="subtitle2">
            {translate('resources.errorDefinitions.dialog.basicInfo')}
          </Typography>
          <HelpPopover text={translate('resources.errorDefinitions.help.basicInfo')} />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <TextField
              label={translate('resources.errorDefinitions.fields.errorCode')}
              value={errorCode}
              onChange={(e) => setErrorCode(e.target.value.toUpperCase())}
              disabled={isEditing}
              required
              sx={{ minWidth: 200 }}
              placeholder="MECH_JAM"
            />
            <HelpPopover text={translate('resources.errorDefinitions.dialog.errorCodeHelp')} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <TextField
              select
              label={translate('resources.errorDefinitions.fields.severity')}
              value={severity}
              onChange={(e) => setSeverity(e.target.value as ErrorSeverity)}
              required
              sx={{ minWidth: 150 }}
            >
              {SEVERITY_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {translate(`resources.errorDefinitions.severity.${s.toLowerCase()}`)}
                </MenuItem>
              ))}
            </TextField>
            <HelpPopover text={translate('resources.errorDefinitions.help.severity')} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <TextField
              select
              label={translate('resources.errorDefinitions.fields.category')}
              value={category}
              onChange={(e) => setCategory(e.target.value as ErrorCategory)}
              required
              sx={{ minWidth: 150 }}
            >
              {CATEGORY_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {translate(`resources.errorDefinitions.category.${c.toLowerCase()}`)}
                </MenuItem>
              ))}
            </TextField>
            <HelpPopover text={translate('resources.errorDefinitions.help.category')} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <TextField
              select
              label={translate('resources.errorDefinitions.fields.nature')}
              value={nature}
              onChange={(e) => setNature(e.target.value as ErrorNature)}
              required
              sx={{ minWidth: 150 }}
            >
              {NATURE_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {translate(`resources.errorDefinitions.nature.${n.toLowerCase()}`)}
                </MenuItem>
              ))}
            </TextField>
            <HelpPopover text={translate('resources.errorDefinitions.help.nature')} />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Conditions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              {translate('resources.errorDefinitions.dialog.conditions')}
            </Typography>
            <HelpPopover text={translate('resources.errorDefinitions.help.conditions')} />
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddCondition}
          >
            {translate('resources.errorDefinitions.dialog.addCondition')}
          </Button>
        </Box>

        {conditions.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {translate('resources.errorDefinitions.dialog.noConditions')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
            {conditions.map((c, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}
              >
                <TextField
                  size="small"
                  label={translate('resources.errorDefinitions.fields.metadataKey')}
                  value={c.metadataKey}
                  onChange={(e) => handleConditionChange(index, 'metadataKey', e.target.value)}
                  required
                  sx={{ minWidth: 180, flex: 1 }}
                  placeholder={translate('resources.errorDefinitions.placeholders.metadataKey')}
                />
                <TextField
                  select
                  size="small"
                  label={translate('resources.errorDefinitions.fields.operator')}
                  value={c.operator}
                  onChange={(e) => handleConditionChange(index, 'operator', e.target.value as MatchConditionOperator)}
                  sx={{ minWidth: 130 }}
                >
                  {OPERATOR_OPTIONS.map((op) => (
                    <MenuItem key={op} value={op}>
                      {translate(`resources.errorDefinitions.operator.${op.toLowerCase()}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label={translate('resources.errorDefinitions.fields.metadataValue')}
                  value={c.metadataValue}
                  onChange={(e) => handleConditionChange(index, 'metadataValue', e.target.value)}
                  required
                  sx={{ minWidth: 180, flex: 1 }}
                  placeholder={translate('resources.errorDefinitions.placeholders.metadataValue')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={c.isActive}
                      onChange={(e) => handleConditionChange(index, 'isActive', e.target.checked)}
                    />
                  }
                  label={translate('resources.errorDefinitions.fields.conditionActive')}
                  sx={{ mt: 0.5, mr: 0 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveCondition(index)}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Translations */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              {translate('resources.errorDefinitions.dialog.translations')}
            </Typography>
            <HelpPopover text={translate('resources.errorDefinitions.help.translations')} />
          </Box>
          {canAddTranslation && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddTranslation}
            >
              {translate('resources.errorDefinitions.dialog.addTranslation')}
            </Button>
          )}
        </Box>

        {translations.length > 0 && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {translations.map((t, index) => {
                  const langInfo = LANGUAGE_OPTIONS.find((l) => l.code === t.language);
                  return (
                    <Tab
                      key={index}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {langInfo?.name || t.language}
                          {t.language === 'en' && (
                            <Typography variant="caption" color="error">*</Typography>
                          )}
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>

            {translations.map((t, index) => (
              <Box
                key={index}
                role="tabpanel"
                hidden={activeTab !== index}
                sx={{ pt: 2 }}
              >
                {activeTab === index && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <TextField
                        select
                        label={translate('resources.errorDefinitions.fields.language')}
                        value={t.language}
                        onChange={(e) => handleTranslationChange(index, 'language', e.target.value as SupportedLanguage)}
                        sx={{ minWidth: 150 }}
                        disabled={t.language === 'en'}
                      >
                        {LANGUAGE_OPTIONS.filter(
                          (l) => l.code === t.language || !usedLanguages.includes(l.code)
                        ).map((l) => (
                          <MenuItem key={l.code} value={l.code}>
                            {l.name}
                          </MenuItem>
                        ))}
                      </TextField>

                      {translations.length > 1 && t.language !== 'en' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveTranslation(index)}
                          sx={{ mt: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <TextField
                        label={translate('resources.errorDefinitions.fields.title')}
                        value={t.title}
                        onChange={(e) => handleTranslationChange(index, 'title', e.target.value)}
                        required={t.language === 'en'}
                        fullWidth
                        placeholder={translate('resources.errorDefinitions.placeholders.title')}
                      />
                      <HelpPopover text={translate('resources.errorDefinitions.dialog.titleHelp')} />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <TextField
                        label={translate('resources.errorDefinitions.fields.description')}
                        value={t.description}
                        onChange={(e) => handleTranslationChange(index, 'description', e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder={translate('resources.errorDefinitions.placeholders.description')}
                      />
                      <HelpPopover text={translate('resources.errorDefinitions.dialog.descriptionHelp')} />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <TextField
                        label={translate('resources.errorDefinitions.fields.recommendation')}
                        value={t.recommendation}
                        onChange={(e) => handleTranslationChange(index, 'recommendation', e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder={translate('resources.errorDefinitions.placeholders.recommendation')}
                      />
                      <HelpPopover text={translate('resources.errorDefinitions.dialog.recommendationHelp')} />
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? translate('ra.action.saving')
            : translate('ra.action.save')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorDefinitionDialog;
